import React, {useContext} from 'react';

import {Group} from '@pb/kit-component-group/';
import {Icons, Sizes} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {withCssModule} from '@pb/kit-util-with-css-module';

import {DialogContext} from '../../context/dialog-context';

import styles from '../../dialog.module.styl';

import type {ButtonProps} from '@pb/kit-component-button';
import type {ComponentPropsWithoutRef} from 'react';

const classNames = withCssModule(styles);

type NativeProps = ComponentPropsWithoutRef<'div'>;

export interface DialogHeaderProps extends NativeProps {
    /**
     * Click handler for the close (`×`) icon button in the header. If omitted,
     * the button still calls `DialogContext.onClose` to close the dialog via the
     * context-provided setter. Pass this to run additional side effects on close.
     */
    onClose?: ButtonProps['onClick'],
    /**
     * Native `<div>` props forwarded to the header root `<Group>` wrapper (e.g.
     * `className`, `style`, `data-testid`). The header applies layout and padding
     * classes; additional `className` is merged, not replaced.
     */
    rootProps?: ComponentPropsWithoutRef<'div'>,
}

/**
 * Titled header bar for `<Dialog>` and `<AlertDialog>` panels.
 *
 * Renders a `<Group>` row containing an ellipsis-truncated title (from `children`)
 * on the left and a close icon button on the right. Clicking the close button
 * calls both `onClose` (if provided) and `DialogContext.onClose` to dismiss the
 * dialog. Use `rootProps` to customize the wrapper element.
 */
export const DialogHeader = ({
    onClose,
    rootProps,
    ...restProps
}: DialogHeaderProps) => {
    const context = useContext(DialogContext);

    return (
        <Group
            alignItems="center"
            gap="m"
            justify="space-between"
            {...rootProps}
            className={classNames('dialog-header pdx-l pdy-m', rootProps?.className)}>
            <div
                {...restProps}
                className={classNames('dialog-header__title font-weight-semi-bold ellipsis', restProps?.className)}
            />
            <BasicIconButton
                aria-label="Close dialog"
                iconProps={{
                    type: Icons.CLOSE,
                    size: Sizes.Size_19,
                }}
                onClick={e => {
                    onClose?.(e);
                    context?.onClose?.();
                }}
            />
        </Group>
    );
};

DialogHeader.displayName = 'DialogHeader';
